import React from "react";
import { Building2, Monitor } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function BranchCounterSelectors() {
  const { t } = useTranslation();
  const { user, updateContext, contextLoading } = useAuth();

  if (!user) return null;

  const branchId = user.default_branch?.id ? String(user.default_branch.id) : "";
  const counterId = user.default_counter?.id ? String(user.default_counter.id) : "";
  const selectedCounter = user.counters?.find((c) => String(c.id) === counterId);
  const shiftOpen = selectedCounter?.shift_status === "open" || user.default_counter?.shift_status === "open";

  async function onBranchChange(value) {
    const branch = user.branches?.find((b) => String(b.id) === value);
    try {
      await updateContext(Number(value), null);
      toast.success(`Branch: ${branch?.name || "updated"}`);
    } catch {
      toast.error("Could not switch branch");
    }
  }

  async function onCounterChange(value) {
    if (!branchId) return;
    try {
      await updateContext(Number(branchId), Number(value));
      const counter = user.counters?.find((c) => String(c.id) === value);
      toast.success(`Counter: ${counter?.name || "updated"}`);
    } catch {
      toast.error("Could not switch counter");
    }
  }

  return (
    <div className="hidden md:flex items-center gap-2">
      <Select value={branchId} onValueChange={onBranchChange} disabled={contextLoading}>
        <SelectTrigger
          className="h-9 w-[150px] text-[12px] bg-slate-50 border-[#E5E7EB]"
          data-testid="branch-selector"
        >
          <Building2 size={13} className="mr-1.5 text-slate-400 shrink-0" />
          <SelectValue placeholder="Branch" />
        </SelectTrigger>
        <SelectContent>
          {(user.branches || []).map((b) => (
            <SelectItem key={b.id} value={String(b.id)} className="text-[12px]">
              {b.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={counterId} onValueChange={onCounterChange} disabled={contextLoading || !branchId}>
        <SelectTrigger
          className="h-9 w-[140px] text-[12px] bg-slate-50 border-[#E5E7EB]"
          data-testid="counter-selector"
        >
          <Monitor size={13} className="mr-1.5 text-slate-400 shrink-0" />
          <SelectValue placeholder="Counter" />
        </SelectTrigger>
        <SelectContent>
          {(user.counters || []).map((c) => (
            <SelectItem key={c.id} value={String(c.id)} className="text-[12px]">
              <span className="flex items-center justify-between gap-2 w-full">
                <span>{c.name}</span>
                <span className={c.shift_status === "open" ? "text-emerald-700 text-[10px] font-medium" : "text-slate-600 text-[10px] font-medium"}>
                  {c.shift_status === "open" ? t("counter.shiftOpen") : t("counter.shiftClosed")}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {counterId && (
        <span
          className={`hidden lg:inline text-[10px] font-semibold px-1.5 py-0.5 rounded ${
            shiftOpen ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
          }`}
          title={t("counter.terminalHint")}
        >
          {shiftOpen ? t("counter.shiftOpen") : t("counter.shiftClosed")}
        </span>
      )}
    </div>
  );
}
